import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { Avatar, Button, Popconfirm, Skeleton, message } from 'antd'
import { CameraOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import B2BLayout from '../../layouts/b2b'
import fetchAPI from '../../../lib/utils/fetch-api'
import { getLoadSessionAction } from '../../store/session/action-creators'
import { useAppDispatch } from '../../hooks/useAppDispatch/useAppDispatch'

interface Profile {
  id: number
  name: string | null
  login: string
  phone: string | null
  avatar_url: string | null
  organization_name: string | null
  office_name: string | null
  roles: { id: number; name: string }[]
}

const MAX_SOURCE_SIZE = 10 * 1024 * 1024
const MAX_AVATAR_SIZE = 2 * 1024 * 1024
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

async function prepareAvatar(file: File): Promise<Blob> {
  const image = await createImageBitmap(file)
  try {
    const scale = Math.min(1, 512 / Math.max(image.width, image.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(image.width * scale))
    canvas.height = Math.max(1, Math.round(image.height * scale))
    const context = canvas.getContext('2d')
    if (!context) throw new Error('Не удалось обработать фотографию.')
    context.drawImage(image, 0, 0, canvas.width, canvas.height)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob || blob.size > MAX_AVATAR_SIZE) {
            reject(new Error('Не удалось подготовить фотографию. Выберите другое изображение.'))
          } else {
            resolve(blob)
          }
        },
        'image/webp',
        0.84,
      )
    })
  } finally {
    image.close()
  }
}

const Page = styled.div`
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
  padding: 16px 0 40px;
  color: #202735;
`

const Intro = styled.div`
  margin-bottom: 28px;

  h1 {
    margin: 0 0 6px;
    font-size: 24px;
    line-height: 32px;
    font-weight: 650;
  }
  p {
    margin: 0;
    color: #747e97;
    font-size: 14px;
    line-height: 21px;
  }
`

const Panel = styled.section`
  border: 1px solid #e8ebf0;
  border-radius: 8px;
  background: #fff;
  margin-bottom: 18px;
  padding: 24px 28px;

  @media (max-width: 620px) {
    padding: 20px 18px;
  }
`

const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 620px) {
    align-items: flex-start;
    gap: 16px;
  }
`

const ProfileAvatar = styled(Avatar)`
  flex: none;
  background: #52c41a;
  font-size: 30px !important;
`

const IdentityBody = styled.div`
  min-width: 0;
  h2 {
    margin: 0 0 4px;
    font-size: 19px;
    line-height: 26px;
    overflow-wrap: anywhere;
  }
  p {
    margin: 0 0 14px;
    color: #747e97;
    line-height: 20px;
    overflow-wrap: anywhere;
  }
  .avatar-help {
    margin: 9px 0 0;
    font-size: 12px;
    color: #747e97;
  }
`

const AvatarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`

const SectionTitle = styled.h2`
  margin: 0 0 7px;
  font-size: 17px;
  line-height: 24px;
  font-weight: 600;
`

const SectionHint = styled.p`
  margin: 0 0 24px;
  color: #747e97;
  font-size: 13px;
  line-height: 20px;
`

const DetailGrid = styled.dl`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px 32px;
  margin: 20px 0 0;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  div {
    min-width: 0;
  }
  dt {
    margin-bottom: 5px;
    color: #747e97;
    font-size: 12px;
    line-height: 18px;
  }
  dd {
    margin: 0;
    font-size: 14px;
    line-height: 21px;
    overflow-wrap: anywhere;
  }
`

const RoleList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
  list-style: none;

  li {
    padding: 7px 11px;
    border: 1px solid #dfe4ed;
    border-radius: 8px;
    background: #f7f8fb;
    color: #394768;
    font-size: 13px;
    line-height: 18px;
  }
`

const ErrorState = styled.div`
  padding: 40px 20px;
  text-align: center;
  color: #747e97;
  p {
    margin-bottom: 16px;
  }
`

const initialsFor = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase()

const display = (value: string | null | undefined) => value?.trim() || 'Не указано'

const MyProfilePage = () => {
  const dispatch = useAppDispatch()
  const fileInput = useRef<HTMLInputElement>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(false)

  const loadProfile = async () => {
    setLoading(true)
    setError(false)
    try {
      const result = await fetchAPI('/api/my-profile', { method: 'GET' })
      setProfile(result.data as Profile)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void loadProfile()
  }, [])

  const onAvatarChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      message.error('Выберите изображение JPG, PNG или WebP.')
      return
    }
    if (file.size > MAX_SOURCE_SIZE) {
      message.error('Размер изображения не должен превышать 10 МБ.')
      return
    }

    setUploading(true)
    try {
      const prepared = await prepareAvatar(file)
      const form = new FormData()
      form.append('avatar', prepared, prepared.type === 'image/png' ? 'avatar.png' : 'avatar.webp')
      const response = await fetch('/api/my-profile/avatar', {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
      const result = await response.json()
      if (!response.ok || !result?.data?.avatar_url) {
        throw new Error(result?.error || 'Не удалось загрузить фотографию.')
      }

      setProfile(current =>
        current ? { ...current, avatar_url: result.data.avatar_url } : current,
      )
      await dispatch(getLoadSessionAction())
      message.success('Фотография профиля обновлена.')
    } catch (uploadError) {
      message.error(
        uploadError instanceof Error ? uploadError.message : 'Не удалось загрузить фотографию.',
      )
    } finally {
      setUploading(false)
    }
  }

  const deleteAvatar = async () => {
    setDeleting(true)
    try {
      const result = await fetchAPI('/api/my-profile/avatar', { method: 'DELETE' }, true)
      if (result.error) {
        throw new Error(result.error.message || 'Не удалось удалить фотографию.')
      }

      setProfile(current => (current ? { ...current, avatar_url: null } : current))
      await dispatch(getLoadSessionAction())
      message.success('Фотография удалена.')
    } catch (deleteError) {
      message.error(
        deleteError instanceof Error ? deleteError.message : 'Не удалось удалить фотографию.',
      )
    } finally {
      setDeleting(false)
    }
  }

  const name = profile?.name?.trim() || profile?.login || 'Пользователь ERP'

  return (
    <B2BLayout
      breadcrumbs={[{ href: '/profile', title: 'Настройки профиля' }]}
      pageTitle="Настройки профиля"
    >
      <Page>
        <Intro>
          <h1>Ваш профиль</h1>
          <p>Здесь можно обновить фотографию и посмотреть свои рабочие данные.</p>
        </Intro>
        {loading ? (
          <Panel>
            <Skeleton active avatar paragraph={{ rows: 4 }} />
          </Panel>
        ) : error || !profile ? (
          <Panel>
            <ErrorState>
              <p>Не удалось загрузить профиль. Попробуйте ещё раз.</p>
              <Button icon={<ReloadOutlined />} onClick={() => void loadProfile()}>
                Повторить
              </Button>
            </ErrorState>
          </Panel>
        ) : (
          <>
            <Panel>
              <Identity>
                <ProfileAvatar size={88} src={profile.avatar_url || undefined}>
                  {initialsFor(name)}
                </ProfileAvatar>
                <IdentityBody>
                  <h2>{name}</h2>
                  <p>{display(profile.office_name)}</p>
                  <input
                    ref={fileInput}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    aria-label="Выбрать фотографию профиля"
                    onChange={onAvatarChange}
                    style={{ display: 'none' }}
                  />
                  <AvatarActions>
                    <Button
                      icon={<CameraOutlined />}
                      loading={uploading}
                      disabled={deleting}
                      onClick={() => fileInput.current?.click()}
                    >
                      {profile.avatar_url ? 'Изменить фото' : 'Загрузить фото'}
                    </Button>
                    {profile.avatar_url && (
                      <Popconfirm
                        title="Удалить фотографию?"
                        description="Вместо фото снова будут показаны инициалы."
                        okText="Удалить"
                        cancelText="Отмена"
                        okButtonProps={{ danger: true }}
                        onConfirm={deleteAvatar}
                      >
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          loading={deleting}
                          disabled={uploading}
                        >
                          Удалить фото
                        </Button>
                      </Popconfirm>
                    )}
                  </AvatarActions>
                  <p className="avatar-help">
                    JPG, PNG или WebP, до 10 МБ. Фото уменьшится автоматически.
                  </p>
                </IdentityBody>
              </Identity>
            </Panel>
            <Panel>
              <SectionTitle>Личные данные</SectionTitle>
              <DetailGrid>
                <div>
                  <dt>ФИО</dt>
                  <dd>{display(profile.name)}</dd>
                </div>
                <div>
                  <dt>Логин (e-mail)</dt>
                  <dd>{display(profile.login)}</dd>
                </div>
                <div>
                  <dt>Телефон</dt>
                  <dd>{display(profile.phone)}</dd>
                </div>
                <div>
                  <dt>Офис</dt>
                  <dd>{display(profile.office_name)}</dd>
                </div>
                <div>
                  <dt>Организация</dt>
                  <dd>{display(profile.organization_name)}</dd>
                </div>
              </DetailGrid>
            </Panel>
            <Panel>
              <SectionTitle>Ваши роли</SectionTitle>
              <SectionHint>Роли определяют, какие разделы ERP вам доступны.</SectionHint>
              {profile.roles.length ? (
                <RoleList>
                  {profile.roles.map(role => (
                    <li key={role.id}>{role.name}</li>
                  ))}
                </RoleList>
              ) : (
                <span>Роли не назначены</span>
              )}
            </Panel>
          </>
        )}
      </Page>
    </B2BLayout>
  )
}

export default MyProfilePage
